import type { ReactNode } from "react";
import type { ViewOptionFields, ViewType } from "../api/types";
import { useT } from "../i18n";
import { useApp, useSyncState } from "../store";
import { setViewOptions, updateProject } from "../sync/actions";
import { command } from "../sync/engine";
import { viewOptionKey } from "../sync/state";
import { IconBoard, IconCalendar, IconCheck, IconList, IconSliders } from "../ui/icons";
import { Popover, useAnchor } from "../ui/primitives";

export interface EffectiveViewOptions {
  mode: "list" | "board" | "calendar";
  groupedBy: string | null;
  sortedBy: string | null;
  sortOrder: "asc" | "desc";
  showCompleted: boolean;
  calendarLayout: "WEEK" | "MONTH";
}

/** Per-user view options, falling back to the project's PVOD and `view_style`. */
export function useViewOptions(viewType: ViewType, objectId: string | null): EffectiveViewOptions {
  const state = useSyncState();
  const vo = state.viewOptions[viewOptionKey(viewType, objectId)];
  const pvod = viewType === "project" && objectId ? state.pvods[objectId] : undefined;
  const project = viewType === "project" && objectId ? state.projects[objectId] : undefined;
  const pick = <K extends keyof ViewOptionFields>(k: K): ViewOptionFields[K] | undefined => {
    const v = vo?.[k];
    if (v !== undefined && v !== null && v !== "") return v;
    const p = pvod?.[k];
    return p !== undefined && p !== null && p !== "" ? p : undefined;
  };
  const mode = (project ? pvod?.view_mode || project.view_style : vo?.view_mode) ?? "list";
  return {
    mode: mode === "board" || mode === "calendar" ? mode : "list",
    groupedBy: (pick("grouped_by") as string | undefined) ?? null,
    sortedBy: (pick("sorted_by") as string | undefined) ?? null,
    sortOrder: (pick("sort_order") as "asc" | "desc" | undefined) ?? "asc",
    showCompleted: Boolean(pick("show_completed_tasks")),
    calendarLayout: (pick("calendar_settings")?.layout as "WEEK" | "MONTH" | undefined) ?? "MONTH",
  };
}

export function setOption(viewType: ViewType, objectId: string | null, fields: ViewOptionFields) {
  if (viewType === "project" && objectId && fields.view_mode) {
    const engine = useApp.getState().engine;
    void updateProject(objectId, { view_style: fields.view_mode });
    void engine?.enqueue(
      command("project_view_options_defaults_set", { project_id: objectId, view_mode: fields.view_mode }),
    );
    const { view_mode: _ignored, ...rest } = fields;
    if (Object.keys(rest).length) void setViewOptions(viewType, objectId, rest);
    return;
  }
  void setViewOptions(viewType, objectId, fields);
}

function Choice({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="menu-item" style={{ justifyContent: "space-between" }}>
      <span>{label}</span>
      <select
        className="input"
        style={{ width: 150, height: 28 }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function ViewOptionsButton({
  viewType,
  objectId,
  layouts = ["list", "board", "calendar"],
  groupings = ["none", "due_date", "added_date", "priority", "label"],
  extra,
}: {
  viewType: ViewType;
  objectId: string | null;
  layouts?: ("list" | "board" | "calendar")[];
  groupings?: string[];
  extra?: ReactNode;
}) {
  const t = useT();
  const a = useAnchor();
  const o = useViewOptions(viewType, objectId);
  const groupLabel: Record<string, string> = {
    none: t("none"),
    due_date: t("groupDate"),
    added_date: t("groupAdded"),
    priority: t("groupPriority"),
    label: t("groupLabel"),
    project: t("groupProject"),
  };
  const set = (f: ViewOptionFields) => setOption(viewType, objectId, f);
  const layoutIcon = {
    list: <IconList size={20} />,
    board: <IconBoard size={20} />,
    calendar: <IconCalendar size={20} />,
  };
  return (
    <>
      <button type="button" className="btn btn-ghost btn-sm" onClick={a.open} data-testid="view-options">
        <IconSliders size={18} />
        {t("view")}
      </button>
      {a.anchor && (
        <Popover anchor={a.anchor} onClose={a.close} placement="bottom-end" className="view-options">
          {layouts.length > 1 && (
            <>
              <div className="menu-title">{t("layout")}</div>
              <div className="menu-row" style={{ padding: "4px 12px 8px" }}>
                {layouts.map((l) => (
                  <button
                    key={l}
                    type="button"
                    className={`btn btn-chip${o.mode === l ? " active" : ""}`}
                    style={{
                      flex: 1,
                      flexDirection: "column",
                      height: 56,
                      borderColor: o.mode === l ? "var(--accent)" : undefined,
                    }}
                    onClick={() => set({ view_mode: l })}
                    data-testid={`layout-${l}`}
                    aria-pressed={o.mode === l}
                  >
                    {layoutIcon[l]}
                    <span className="small">{t(l)}</span>
                  </button>
                ))}
              </div>
              <div className="menu-sep" />
            </>
          )}
          <label className="menu-item" style={{ justifyContent: "space-between" }}>
            <span>{t("showCompleted")}</span>
            <input
              type="checkbox"
              checked={o.showCompleted}
              onChange={(e) => set({ show_completed_tasks: e.target.checked })}
              aria-label={t("showCompleted")}
            />
          </label>
          <div className="menu-sep" />
          <div className="menu-title">{t("sorting")}</div>
          {o.mode !== "calendar" && (
            <Choice
              label={t("grouping")}
              value={o.groupedBy ?? "none"}
              options={groupings.map((g) => ({ value: g, label: groupLabel[g] ?? g }))}
              onChange={(v) => set({ grouped_by: v === "none" ? null : v })}
            />
          )}
          <Choice
            label={t("sorting")}
            value={o.sortedBy ?? "manual"}
            options={[
              { value: "manual", label: t("manual") },
              { value: "due_date", label: t("sortDate") },
              { value: "added_date", label: t("sortAdded") },
              { value: "priority", label: t("sortPriority") },
              { value: "alphabetically", label: t("sortName") },
            ]}
            onChange={(v) => set({ sorted_by: v === "manual" ? null : v })}
          />
          {o.sortedBy && o.sortedBy !== "manual" && (
            <Choice
              label={t("sorting")}
              value={o.sortOrder}
              options={[
                { value: "asc", label: t("ascending") },
                { value: "desc", label: t("descending") },
              ]}
              onChange={(v) => set({ sort_order: v as "asc" | "desc" })}
            />
          )}
          {o.mode === "calendar" && (
            <Choice
              label={t("calendar")}
              value={o.calendarLayout}
              options={[
                { value: "WEEK", label: t("week") },
                { value: "MONTH", label: t("month") },
              ]}
              onChange={(v) => set({ calendar_settings: { layout: v as "WEEK" | "MONTH" } })}
            />
          )}
          {extra}
          <div style={{ height: 4 }} />
          <span hidden>
            <IconCheck />
          </span>
        </Popover>
      )}
    </>
  );
}
